import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { fileToBase64 } from "@/lib/utils-app";

/**
 * Captura de muestras adicionales del rostro (varios angulos) para mejorar la
 * precision del reconocimiento facial. Devuelve un array de data URLs.
 */
export default function FaceSamplesField({
  value = [],
  onChange,
  maxSamples = 3,
  existingCount = 0,
  label = "Muestras adicionales de rostro",
  hint = "Captura 2 o 3 fotos de frente y de lado, con buena luz. Mejoran el reconocimiento y reducen confusiones.",
}) {
  const samples = Array.isArray(value) ? value : [];
  const [hasCamera, setHasCamera] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => setHasCamera(devices.some((d) => d.kind === "videoinput")))
      .catch(() => setHasCamera(false));
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  const remaining = Math.max(0, maxSamples - samples.length);

  const addSample = (dataUrl) => {
    if (!dataUrl) return;
    if (samples.length >= maxSamples) {
      toast.warning(`Maximo ${maxSamples} muestras`);
      return;
    }
    onChange([...samples, dataUrl]);
  };

  const removeSample = (idx) => {
    onChange(samples.filter((_, i) => i !== idx));
  };

  const openCamera = async () => {
    if (remaining <= 0) {
      toast.warning(`Maximo ${maxSamples} muestras`);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Este navegador no permite usar la camara");
      return;
    }
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = media;
      setStream(media);
      setCameraOpen(true);
    } catch {
      toast.error("No se pudo acceder a la camara. Revisa permisos del navegador.");
    }
  };

  useEffect(() => {
    if (cameraOpen && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [cameraOpen, stream]);

  const closeCamera = () => {
    stopStream();
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      toast.error("Espera a que la camara este lista");
      return;
    }
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    addSample(canvas.toDataURL("image/jpeg", 0.88));
    toast.success("Muestra capturada");
    if (samples.length + 1 >= maxSamples) closeCamera();
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona una imagen");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no debe superar 5 MB");
      return;
    }
    try {
      addSample(await fileToBase64(file));
      toast.success("Muestra agregada");
    } catch {
      toast.error("No se pudo leer la imagen");
    }
  };

  return (
    <>
      <div>
        {label && (
          <label className="text-xs uppercase tracking-widest text-white/60">{label}</label>
        )}
        <div className={`flex items-center gap-3 flex-wrap ${label ? "mt-2" : ""}`}>
          {samples.map((src, idx) => (
            <div key={idx} className="relative h-16 w-16 rounded-sm overflow-hidden bg-white/5 shrink-0">
              <img src={src} alt={`muestra ${idx + 1}`} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeSample(idx)}
                className="absolute top-0 right-0 bg-black/70 text-red-300 p-0.5"
                aria-label="Quitar muestra"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {remaining > 0 && (
            <div className="flex flex-wrap gap-2">
              {hasCamera && (
                <button type="button" onClick={openCamera} className="btn-outline text-xs py-2">
                  <Camera size={12} /> Tomar muestra
                </button>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-outline text-xs py-2"
              >
                <Upload size={12} /> Subir muestra
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} />
            </div>
          )}
        </div>
        {hint && <p className="text-[10px] text-white/40 mt-2">{hint}</p>}
        {existingCount > 0 && samples.length === 0 && (
          <p className="text-[10px] text-green-400/70 mt-1">
            Ya hay {existingCount} muestra(s) guardada(s). Captura nuevas solo si quieres reemplazarlas.
          </p>
        )}
      </div>

      {cameraOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={closeCamera}
        >
          <div className="glass-strong max-w-lg w-full rounded-sm p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-display text-xl text-white">
                Tomar muestra ({samples.length}/{maxSamples})
              </span>
              <button type="button" onClick={closeCamera} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="relative aspect-[4/3] bg-black rounded-sm overflow-hidden">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={closeCamera} className="btn-outline text-sm py-2">
                Listo
              </button>
              <button
                type="button"
                onClick={capturePhoto}
                disabled={remaining <= 0}
                className="btn-primary text-sm py-2 disabled:opacity-40"
              >
                <Camera size={14} /> Capturar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
